import { useState, type CSSProperties, type ReactNode } from 'react'
import { DotsSixVerticalIcon } from '@phosphor-icons/react'
import { ArrowDown, ArrowUp, NavArrowLeft, Xmark } from 'iconoir-react'
import { ItemTypes } from 'app/components/sortable-dnd/ItemTypes'
import { useSortableItem } from 'app/components/sortable-dnd/useSortableItem'
import { cn } from 'app/lib/utils'
import type { BackgroundPreset, FontPreset } from 'app/lib/forms/appearance'
import { accentForeground, BACKGROUND_PRESETS, FONT_PRESETS } from 'app/lib/forms/appearance'
import { HEX_COLOR } from 'app/lib/forms/builder'
import { PacketRunner } from 'app/components/form-runtime/PacketRunner'
import { PageScroll } from 'app/components/layout/PageScroll'
import Spinner from 'app/components/Spinner'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select'
import { Switch } from 'app/components/ui/switch'
import { Textarea } from 'app/components/ui/textarea'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { Field } from 'app/components/workflows/inspectors/fields'
import { EditorModeToggle, PreviewPane } from './EditorPreview'
import type { UsePacketEditorReturn } from './usePacketEditor'

const BACKGROUND_LABELS: Record<BackgroundPreset, string> = {
  white: `White`,
  warm: `Warm`,
  cool: `Cool`,
  paper: `Paper`,
}
/** Common practice brand hues. Any hex still works in the field beside them. */
const ACCENT_SWATCHES = [`#1d4ed8`, `#0f766e`, `#15803d`, `#7c3aed`, `#be185d`, `#b91c1c`, `#c2410c`, `#facc15`]
const FONT_LABELS: Record<FontPreset, string> = { system: `Clean`, classic: `Classic`, modern: `Modern` }
const LOGO_HINTS: Record<UsePacketEditorReturn['logoStatus'], string | undefined> = {
  none: undefined,
  checking: `Checking the logo…`,
  found: `Uses this location's online scheduling logo.`,
  missing: `This location has no online scheduling logo, so the practice name is shown instead.`,
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-4 rounded-xl border border-default bg-surface p-4 shadow-elevation-1">
      <h2 className="text-sm font-semibold text-strong">{title}</h2>
      {children}
    </section>
  )
}

/** Typing a hex code passes through invalid states, so the field keeps its own text and only
 *  hands a complete colour up. */
function AccentField({ value, onChange }: { value: string; onChange: (color: string) => void }) {
  const [text, setText] = useState(value)
  const commit = (next: string) => {
    setText(next)
    if (HEX_COLOR.test(next)) onChange(next)
  }
  return (
    <Field
      label="Accent colour"
      hint="Button text switches between dark and light on its own, so it always stays readable."
    >
      <div className="flex flex-wrap items-center gap-2">
        {ACCENT_SWATCHES.map((swatch) => (
          <Button
            key={swatch}
            variant="toggle"
            size="icon-sm"
            aria-label={`Accent ${swatch}`}
            aria-pressed={value.toLowerCase() === swatch}
            onClick={() => commit(swatch)}
          >
            <span className="size-4 rounded-full bg-(--swatch)" style={{ '--swatch': swatch } as CSSProperties} />
          </Button>
        ))}
        <Input
          inline
          aria-label="Accent colour hex"
          className="w-28 font-mono"
          value={text}
          onChange={(e) => commit(e.target.value)}
        />
        <span
          className="rounded-md bg-(--swatch) px-3 py-1.5 text-xs font-medium text-(--swatch-fg)"
          style={{ '--swatch': value, '--swatch-fg': accentForeground(value) } as CSSProperties}
        >
          Continue
        </span>
      </div>
    </Field>
  )
}

function PacketFormRow({
  formId,
  index,
  name,
  version,
  latestVersion,
  isLast,
  onReorder,
  onPinLatest,
  onRemove,
}: {
  formId: string
  index: number
  name: string | undefined
  version: number
  latestVersion: number | undefined
  isLast: boolean
  onReorder: (from: number, to: number) => void
  onPinLatest: () => void
  onRemove: () => void
}) {
  const { rowRef, handleRef, isDragging, handlerId } = useSortableItem({
    type: ItemTypes.PACKET_FORM,
    id: formId,
    index,
    onMove: onReorder,
  })
  return (
    <li>
      <div
        ref={rowRef}
        data-handler-id={handlerId}
        className={cn(
          `flex items-center gap-2 rounded-lg border border-default bg-surface px-2 py-2 text-sm`,
          isDragging && `opacity-40`,
        )}
      >
        <span
          ref={(node) => {
            handleRef(node)
          }}
          aria-label="Drag to reorder"
          className="flex h-8 w-5 shrink-0 cursor-grab items-center justify-center text-minimal hover:text-subtle"
        >
          <DotsSixVerticalIcon size={16} weight="bold" />
        </span>
        <span className="w-5 text-xs text-minimal tabular-nums">{index + 1}</span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium text-strong">{name ?? `Unavailable form`}</span>
          <span className="text-xs text-subtle">{`Version ${version}`}</span>
        </span>
        {latestVersion !== undefined && latestVersion > version && (
          <Button variant="link" size="inline" onClick={onPinLatest}>
            {`Use v${latestVersion}`}
          </Button>
        )}
        <Button
          variant="subtle"
          size="icon-sm"
          aria-label="Move up"
          disabled={index === 0}
          onClick={() => onReorder(index, index - 1)}
        >
          <ArrowUp />
        </Button>
        <Button
          variant="subtle"
          size="icon-sm"
          aria-label="Move down"
          disabled={isLast}
          onClick={() => onReorder(index, index + 1)}
        >
          <ArrowDown />
        </Button>
        <Button variant="subtle" size="icon-sm" aria-label="Remove form" onClick={onRemove}>
          <Xmark />
        </Button>
      </div>
    </li>
  )
}

export function PacketEditor(props: UsePacketEditorReturn) {
  const { fetching, notFound, draft, dirty, saving, forms, pinned, locations, locationId, logoStatus, preview } = props
  const [previewing, setPreviewing] = useState(false)

  if (notFound) {
    return (
      <TableEmptyState
        title="Packet not found"
        description="It may have been deleted."
        action={
          <Button variant="white" size="sm" onClick={props.back}>
            Back to packets
          </Button>
        }
      />
    )
  }
  if (fetching || !draft || !preview) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  const welcome = draft.welcome
  const available = forms.filter((f) => !draft.forms.some((ref) => ref.formId === f.formId))

  const header = (
    <div className="flex flex-col gap-3 pb-4">
      <Button variant="subtle" size="inline" className="self-start" onClick={props.back}>
        <NavArrowLeft /> Packets
      </Button>
      <div className="flex flex-wrap items-center gap-3">
        <Input
          aria-label="Packet name"
          className="h-10 max-w-md flex-1 text-lg font-semibold"
          value={draft.name}
          onChange={(e) => props.setField(`name`, e.target.value)}
        />
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <EditorModeToggle previewing={previewing} onChange={setPreviewing} />
          {dirty && <span className="text-xs text-subtle">Unsaved changes</span>}
          <Button variant="primary" size="sm" disabled={!dirty || saving} onClick={props.save}>
            Save
          </Button>
        </div>
      </div>
    </div>
  )

  return (
    <PageScroll header={header}>
      {previewing ? (
        <PreviewPane background={BACKGROUND_PRESETS[draft.appearance.backgroundPreset]}>
          {preview.forms.length > 0 ? (
            <PacketRunner packet={preview} onSubmitForm={async () => undefined} onComplete={() => undefined} />
          ) : (
            <div className="flex h-full items-center justify-center p-6 text-center text-sm text-subtle">
              Add a form to see the packet.
            </div>
          )}
        </PreviewPane>
      ) : (
        <div className="flex max-w-3xl flex-col gap-4 pb-10">
          <Section title="Forms">
            {draft.forms.length === 0 && (
              <p className="text-sm text-subtle">Add the forms a patient fills in, in the order they fill them in.</p>
            )}
            <ol className="flex flex-col gap-2">
              {draft.forms.map((ref, index) => (
                <PacketFormRow
                  key={ref.formId}
                  formId={ref.formId}
                  index={index}
                  name={pinned[index]?.name}
                  version={ref.formVersion}
                  latestVersion={forms.find((f) => f.formId === ref.formId)?.latest.formVersion}
                  isLast={index === draft.forms.length - 1}
                  onReorder={props.moveForm}
                  onPinLatest={() => props.pinLatest(index)}
                  onRemove={() => props.removeForm(index)}
                />
              ))}
            </ol>
            {available.length > 0 ? (
              <Select value="" onValueChange={props.addForm}>
                <SelectTrigger aria-label="Add a form" className="sm:max-w-xs">
                  <SelectValue placeholder="Add a form" />
                </SelectTrigger>
                <SelectContent>
                  {available.map((f) => (
                    <SelectItem key={f.formId} value={f.formId}>
                      {f.latest.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              forms.length === 0 && (
                <p className="text-sm text-subtle">Publish a form under the Forms tab to add it here.</p>
              )
            )}
          </Section>

          <Section title="Practice">
            <Field
              label="Location"
              info="Fills in the name, phone, and logo from this location. You can change the name and phone after."
            >
              <Select
                value={locationId ?? ``}
                onValueChange={(id) => {
                  const location = locations.find((l) => l.id === id)
                  if (location) props.chooseLocation(location)
                }}
              >
                <SelectTrigger aria-label="Location">
                  <SelectValue placeholder="Choose a location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((location) => (
                    <SelectItem key={location.id} value={location.id}>
                      {location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {LOGO_HINTS[logoStatus] && <p className="mt-1.5 text-xs text-subtle">{LOGO_HINTS[logoStatus]}</p>}
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Practice name">
                <Input
                  value={draft.branding.displayName}
                  onChange={(e) => props.setField(`branding`, { ...draft.branding, displayName: e.target.value })}
                />
              </Field>
              <Field label="Phone" hint="Shown at the bottom of every page so a patient can call for help.">
                <Input
                  value={draft.branding.phone ?? ``}
                  onChange={(e) =>
                    props.setField(`branding`, { ...draft.branding, phone: e.target.value || undefined })
                  }
                />
              </Field>
            </div>
          </Section>

          <Section title="Appearance">
            <AccentField
              value={draft.appearance.accentColor}
              onChange={(accentColor) => props.setField(`appearance`, { ...draft.appearance, accentColor })}
            />
            <Field label="Background">
              <div className="flex flex-wrap gap-2">
                {(Object.keys(BACKGROUND_PRESETS) as BackgroundPreset[]).map((preset) => (
                  <Button
                    key={preset}
                    variant="toggle"
                    size="sm"
                    aria-pressed={draft.appearance.backgroundPreset === preset}
                    onClick={() => props.setField(`appearance`, { ...draft.appearance, backgroundPreset: preset })}
                  >
                    <span
                      className="size-4 rounded-full border border-default bg-(--swatch)"
                      style={{ '--swatch': BACKGROUND_PRESETS[preset] } as CSSProperties}
                    />
                    {BACKGROUND_LABELS[preset]}
                  </Button>
                ))}
              </div>
            </Field>
            <Field label="Font">
              <div className="flex flex-wrap gap-2">
                {(Object.keys(FONT_PRESETS) as FontPreset[]).map((preset) => (
                  <Button
                    key={preset}
                    variant="toggle"
                    size="sm"
                    aria-pressed={draft.appearance.fontPreset === preset}
                    onClick={() => props.setField(`appearance`, { ...draft.appearance, fontPreset: preset })}
                  >
                    <span
                      className="font-(family-name:--preset-font)"
                      style={{ '--preset-font': FONT_PRESETS[preset] } as CSSProperties}
                    >
                      {FONT_LABELS[preset]}
                    </span>
                  </Button>
                ))}
              </div>
            </Field>
          </Section>

          <Section title="Screens">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-default">Welcome screen before the first form</span>
              <Switch aria-label="Welcome screen" checked={!!draft.welcome} onCheckedChange={props.toggleWelcome} />
            </div>
            {welcome && (
              <div className="flex flex-col gap-3">
                <Field label="Welcome title">
                  <Input
                    value={welcome.title}
                    onChange={(e) => props.setField(`welcome`, { ...welcome, title: e.target.value })}
                  />
                </Field>
                <Field label="Welcome message">
                  <Textarea
                    rows={3}
                    value={welcome.body}
                    onChange={(e) => props.setField(`welcome`, { ...welcome, body: e.target.value })}
                  />
                </Field>
              </div>
            )}
            <Field label="Completion title">
              <Input
                value={draft.completion.title}
                onChange={(e) => props.setField(`completion`, { ...draft.completion, title: e.target.value })}
              />
            </Field>
            <Field label="Completion message">
              <Textarea
                rows={3}
                value={draft.completion.body}
                onChange={(e) => props.setField(`completion`, { ...draft.completion, body: e.target.value })}
              />
            </Field>
          </Section>
        </div>
      )}
    </PageScroll>
  )
}
